import React, {FC, useEffect, useState, MouseEvent, ChangeEvent}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';

// import MUI.
import {Avatar, Button, Grid, Typography, Link, Box, Paper, TextField, MenuItem, IconButton, Checkbox, FormControlLabel} from '@mui/material';
import {Menu as MenuIcon, AccountCircle as AccountCircleIcon, 
  Home as HomeIcon, ManageAccounts as ManageAccountsIcon, Logout as LogoutIcon } from '@mui/icons-material';

  import { DatePicker } from '@mui/x-date-pickers';

// import library.
import { useFieldArray, useForm, useFormContext, Controller, FormProvider, FieldArrayWithId, ArrayPath } from 'react-hook-form';
import {useQuery,useQueryClient, QueryClient, QueryClientProvider, useMutation, } from "react-query";
import { useSnackbar } from 'notistack';
import { useTranslation  } from 'react-i18next';
import md5 from 'md5';

// import user Component
import BasicSelectList from '../../ui/BasicSelectList';

import EnhancedTable, {EnhancedTableProps, HeadCell,EnhancedTableToolbarProps } from '../../ui/EnhancedTable';

import {FormDialog} from '../../ui/FormDialog';

// import user Library.
import IBankUser, {defaultBankUser} from './model/BankUser';
import IBankRole from './model/BankRole';
import {IEnumerationItem} from '../configuration/model/Enumeration';

import useBankUserService from './service/BankUser';
import useBankRoleService from './service/BankRole';
import useGroupPolicyService from './service/GroupPolicy';
import useEnumerationService from '../configuration/service/Enumeration';

import {useUserStore} from '../../library/store'; 
import IGroupPolicy from './model/GroupPolicy';


const BankUserForm: FC<IBankUser> = (props : IBankUser = defaultBankUser) => {

  const { setCurrentEntity,currentUser,setIsBackendOperation, setCurrentObjectDataDef, setCurrentObjectKey} = useUserStore();

  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const {userID} = useParams();    

  const [_userID, _setUserID] = useState<number>( Number( userID || 0 ) );

  const methods = useForm<IBankUser>({defaultValues:defaultBankUser});
  const { register, setValue ,getValues,watch, reset, control , formState: { errors } } = methods;

  let {fields:groupPolicies  , append: appendGroupPolicy, remove: removeGroupPolicy} = useFieldArray<IBankUser,'groupPolicies'>({ control, name: 'groupPolicies'});
  let {fields:roles  , append: appendRole, remove: removeRole} = useFieldArray<IBankUser,'roles'>({ control, name: 'roles'});
  let {fields:agencies  , append: appendAgency, remove: removeAgency} = useFieldArray<IBankUser,'agencies'>({ control, name: 'agencies'});

  const {getUsersList, saveBankUser, getBankUser} = useBankUserService();
  const {getRolesList} = useBankRoleService();
  const {getGroupPoliciesList, getAgenciesList} = useGroupPolicyService();
  const {getEnumerationItemsList} = useEnumerationService();

  const {isSuccess: isRolesSuccess, status: rolesStatus,data: allRoles} = useQuery<IBankRole[]>("roles",getRolesList);
  const {isSuccess: isGroupPoliciesSuccess, status: groupPoliciesStatus,data: allGroupPolicies} = useQuery<IGroupPolicy[]>("groupPolicies",getGroupPoliciesList);

  const {isSuccess: isAgenciesSuccess, status: agenciesStatus,data: allAgencies} = useQuery<IEnumerationItem[]>("agencies",getAgenciesList);
  const {isSuccess: isServicesSuccess, status: servicesStatus,data: allServices} = useQuery<IEnumerationItem[]>("services", () => getEnumerationItemsList('SERVICE'));

  const {isSuccess: isUsersSuccess, status: usersStatus,data: users} = useQuery<IBankUser[]>("users",getUsersList);
  const [openUserList, setOpenUserList] = useState(false);

  const queryClient = useQueryClient();
    const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IBankUser,Error,IBankUser>(saveBankUser, 
      {        
        onSuccess: (data: IBankUser) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
            
          //reset(data);
          setValue('userID', data.userID);
          queryClient.invalidateQueries(['BankUser',data.userID]);
          queryClient.invalidateQueries('users');
        },
        onError: (err: Error) => {          
          enqueueSnackbar( 'error', { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
        }
      });

  const {data: _data} = useQuery<IBankUser>(['BankUser', _userID], () => getBankUser( _userID  ), 
      {enabled: _userID > 0 } );

  useEffect( () => {
    setCurrentEntity(t('BankUser'));
    setCurrentObjectDataDef('10031'); //
    setCurrentObjectKey( _userID<=0? '':_userID.toString());

    if( currentUser.userMenus.find( x => x.entity === 'BankPolicy') === undefined)
        navigate('/');
  }, [_userID] );

  useEffect( () => { 
    const data = getValues();
    const tmpUserID = data.userID || 0;
   
    if(tmpUserID !== _userID) {
      
      reset(_data);
    }
     
  }, [_data, reset]);

  useEffect( () => {
    setIsBackendOperation(isLoading);
  }, [isLoading]);

  // useEffect( () => { 
       
  //   if(_userID <= 0) {
  //     reset(defaultBankUser);
  //   }
     
  // }, [_userID]);

  const [headUsereCells, setHeadUserCells]  = useState<HeadCell<IBankUser>[]>([
    {id:'userID', label : t('ID'),  display: true, type: 'string'},
    {id:'login', label : t('Login'),  display: true, type: 'string'},
    {id:'userName', label : t('Name'),  display: true, type: 'string'},    
    {id:'userDescription', label : t('Description'),  display: true, type: 'string'},
]);

  //const handleBankRoleCheckedChange = (event: ChangeEvent<HTMLInputElement>,checked: boolean, item: IBankRole ) => {
  const handleBankRoleCheckedChange = (checked: boolean, item: IBankRole ) => {
 
    const index = roles.findIndex( x => x.roleID === item.roleID);
    if(!checked && index >= 0)
      removeRole(index);
    else if(checked && index < 0)
      appendRole({roleID: item.roleID});
  }

  //const handleGroupPolicyCheckedChange = (event: ChangeEvent<HTMLInputElement>,checked: boolean, item: IGroupPolicy ) => {
  const handleGroupPolicyCheckedChange = (checked: boolean, item: IGroupPolicy ) => {

    const index = groupPolicies.findIndex( x => x.groupPolicyID === item.groupPolicyID);
    if(!checked && index >= 0)
      removeGroupPolicy(index);
    else if(checked && index < 0)
      appendGroupPolicy({groupPolicyID: item.groupPolicyID});
  }

  //const handleAgencyCheckedChange = (event: ChangeEvent<HTMLInputElement>,checked: boolean, item: IEnumerationItem ) => {
  const handleAgencyCheckedChange = (checked: boolean, item: IEnumerationItem ) => {

    const index = agencies.findIndex( x => x.agencyCode === item.enumerationItemCode);
    if(!checked && index >= 0)
      removeAgency(index);
    else if(checked && index < 0)
      appendAgency({agencyCode: item.enumerationItemCode});
  }

  const handleBankUserRowDoubleClick = (event: React.MouseEvent<unknown>, bankUser: IBankUser) => {
    setOpenUserList(false);
    const selectedUserID = bankUser.userID || 0;
    
    if(selectedUserID > 0) 
      _setUserID(selectedUserID);
  }

  const saveData = async (event: MouseEvent<HTMLButtonElement>) => {

    const data = getValues();   
    if(data.password !== data.passwordConfirm) {
        enqueueSnackbar( t('Password and confirmation are not the same'), { variant: 'success',
          anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
        return;
    }

    if(data.password !== 'DEFAULT_PASSWORD') {
      data.password = md5(data.password);
      data.passwordConfirm = md5(data.password);
    }

    mutate(data);
  }

  const listData = async (event: MouseEvent<HTMLButtonElement>) => {
    //navigate('bankRoleList');
    setOpenUserList(true);
  }

    return (
      <>
        <FormProvider {...methods} >
          <Grid container component="main" sx={{  width: '100%' }} alignItems="flex-start">
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} square>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }} >
                <Box sx={{ mt: 1, width: '100%' }} >                                       
                  <TextField  sx={{width:'calc(50% - 8px)'}} id="userID" label="User ID" {...register('userID')} disabled/>
                  <Controller control={control}
                    name='creationDate' 
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <DatePicker label='Date Creation' 
                        onChange={onChange}                        
                        value={value}
                        renderInput={(params) => <TextField {...params} sx={{width:'calc(50% - 8px)'}} />}
                      /> )}
                  />                   
                  <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                  <Button id='btnList' onClick={listData} sx={ {display:'none'}}  />
                </Box>     
                <Box sx={{ mt: 1, width: '100%' }} >                                       
                  <TextField  sx={{width:'calc(100% - 8px)'}} id="login" label={t('Login')} {...register('login')}
                    inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />     
                </Box>    
                <Box sx={{ mt: 1, width: '100%' }} >                                       
                  <TextField  sx={{width:'calc(50% - 8px)'}} type="password" id="password" label={t('Password')} {...register('password')}
                    inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>     
                  <TextField  sx={{width:'calc(50% - 8px)'}} type="password" id="passwordConfirm" label={t('Password Confirm')} {...register('passwordConfirm')}
                    inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>     
                </Box>       
              </Box> 
            </Grid>        
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} square>
              <Box sx={{ mt: 1, width: '100%' }} >                                       
                <TextField  sx={{width:'calc(100% - 8px)'}} id="userName" label={t('Name')} {...register('userName')}/>     
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >                                       
                <TextField  sx={{width:'calc(100% - 8px)'}} id="userDescription" label={t('Description')} {...register('userDescription')}/>     
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >                                       
                <TextField  sx={{width:'calc(100% - 8px)'}} id="roleName" label=" " disabled />     
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} square>
              <Box sx={{ mt: 1, width: '100%' }} >                                       
                <TextField  sx={{width:'calc(100% - 8px)'}} id="emailAddress" label={t('Email')} {...register('emailAddress')}
                  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />     
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >  
                <Controller name='serviceCode' control={control} defaultValue=''
                      render={ ({field: {onChange, value}}) => (
                        <TextField select onChange={onChange} value={value} sx={{width:'calc(100% - 8px)'}} id="serviceCode" label={t('Service')}>
                        {allServices ? allServices.map( 
                          (sm,idx) => <MenuItem key={sm.enumerationItemCode} value={sm.enumerationItemCode}>{sm.enumerationItemName}</MenuItem> ):
                          <MenuItem value=''>''</MenuItem>
                        }
                        </TextField>
                      )}
                    />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >     
              
              <FormControlLabel sx={{width:'calc(30% - 8px)'}}
                control={
                  <Controller
                      name='accountEnabled'
                      control={control}
                      render={({ field: {onChange, value} }) => <Checkbox onChange={onChange} checked={value} />}
                    />  }
                  label={t('Enabled ?')}
                />
                                     
                <Controller control={control}
                  name='enabledFrom' 
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Start Date')}
                      onChange={onChange}                        
                      value={value}
                      renderInput={(params) => <TextField {...params} sx={{width:'calc(35% - 8px)'}} />}
                    /> )}
                />
                <Controller control={control}
                  name='enabledTo' 
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('End Date')}
                      onChange={onChange}                        
                      value={value}
                      renderInput={(params) => <TextField {...params} sx={{width:'calc(35% - 8px)'}} />}
                    /> )}
                />    
              </Box>
            </Grid> 
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} sx={ {height: 400} } square>
              {allRoles && <BasicSelectList<IBankRole>  items={allRoles} title={t('Roles')}
                  valueKey='roleID' displayKey='roleName' initialItemsSelected={roles.map( (x) => (x.roleID))}
                  onItemCheckedChange={handleBankRoleCheckedChange}  /> }
            </Grid> 
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} sx={ {height: 400} } square>
              {allGroupPolicies && <BasicSelectList<IGroupPolicy>  items={allGroupPolicies} title={t('Group Policies')}
                  valueKey='groupPolicyID' displayKey='groupContractName' initialItemsSelected={groupPolicies.map( (x) => (x.groupPolicyID))}
                  onItemCheckedChange={handleGroupPolicyCheckedChange}  /> }
            </Grid>
            <Grid item xs={12} sm={6} md={4} component={Paper} elevation={0} sx={ {height: 400} } square>
              {allAgencies && <BasicSelectList<IEnumerationItem>  items={allAgencies} title={t('Agencies')}
                  valueKey='enumerationItemCode' displayKey='enumerationItemName' initialItemsSelected={agencies.map( (x) => (x.agencyCode))}
                  onItemCheckedChange={handleAgencyCheckedChange}  /> }
            </Grid>
          </Grid>
          { openUserList && <FormDialog open={openUserList} okText='' cancelText='' title={t('Select User')} maxWidth='md'
            onCancel={()=> {setOpenUserList(false);}} 
            onClose={()=> {setOpenUserList(false);}} onOk={() => {}}  >
            <Box sx={{ mt: 1, width: '100%' }} >
              {users && <EnhancedTable<IBankUser>  rows={users} headCells={headUsereCells} order='asc' orderBy='login'
              title={t('Users')} objKey='userID' 
                onRowDoubleClick={handleBankUserRowDoubleClick}/> }
            </Box>
          </FormDialog> }
        </FormProvider>
      </>
    );
  }
  
  export default BankUserForm;